import type { Variant } from '../../gameTypes.js';
import type { JumpPatternRng } from './jumpPatternRng.js';
import type { JumpStamper } from './jumpPatternStamp.js';

/**
 * One element of the rhythmic spine the composer walks: a musical onset the
 * player presses on. `kind` carries the source osu hit type — a `slider` /
 * `spinner` is a sustained note, which biases the selector toward a ride/
 * platform choreography, while a `circle` is a tap. `endBeat` is the sustain
 * end (equals `beat` for a circle).
 */
export interface JumpAnchor {
  beat: number;
  kind: 'circle' | 'slider' | 'spinner';
  endBeat: number;
}

/** Difficulty ordering for unlock gating (`insane` isn't used by Jump). */
export const TIER_RANK: Record<Variant, number> = { normal: 0, hard: 1, extreme: 2 };

/** True when `tier` is at least as hard as `min` — the pattern/twist unlock test. */
export function tierAtLeast(tier: Variant, min: Variant): boolean {
  return TIER_RANK[tier] >= TIER_RANK[min];
}

/**
 * Everything a pattern needs to choreograph the next run of onsets. Patterns
 * speak in press beats (read from `anchors`) and paint through `stamper`; the
 * musical context (`progress`, `inKiai`) and tier dials steer how busy and how
 * bold the choreography is. All four RNG streams are seeded per (slug, variant)
 * so the whole chart stays deterministic.
 */
export interface JumpPatternContext {
  readonly stamper: JumpStamper;
  /** The full filtered onset spine (already ≥ `minGapBeats` apart). */
  readonly anchors: readonly JumpAnchor[];
  /** Index of the onset this pattern starts on. */
  readonly index: number;
  readonly tier: Variant;
  /** Chart position 0..1 at the starting onset (ramps intensity). */
  readonly progress: number;
  /** Whether the starting onset is inside a kiai (the song's drops). */
  readonly inKiai: boolean;
  readonly rng: JumpPatternRng;
  readonly minGapBeats: number;
  readonly maxClusterCols: number;
  readonly cellBeats: number;
  readonly totalBeats: number;
}

/** The outcome of painting a pattern: how many onsets it claimed (≥ 1). */
export interface PatternPlacement {
  consumed: number;
}

/** Beat of the onset `k` steps ahead of the cursor, or `null` past the end. */
export function anchorBeatAhead(ctx: JumpPatternContext, k: number): number | null {
  const anchor = ctx.anchors[ctx.index + k];
  return anchor ? anchor.beat : null;
}

/** How many onsets remain from the cursor (inclusive of the current one). */
export function remainingAnchors(ctx: JumpPatternContext): number {
  return ctx.anchors.length - ctx.index;
}

/**
 * Beats of clear runway after the onset `k` steps ahead — i.e. to the next onset
 * (or to the chart end if it's the last). Terrain patterns size their footprint
 * against this so a platform or staircase never overruns its neighbour.
 */
export function gapAfterAnchor(ctx: JumpPatternContext, k: number): number {
  const here = ctx.anchors[ctx.index + k];
  if (!here) {
    return 0;
  }
  const next = ctx.anchors[ctx.index + k + 1];
  return (next ? next.beat : ctx.totalBeats) - here.beat;
}

/**
 * A reusable obstacle choreography. Concrete patterns are CLASSES (per repo
 * convention for strategies) registered once in the composer. Each declares its
 * unlock tier and a base selection weight, decides its own eligibility given the
 * live context, and paints into the grid — claiming one or more onsets and
 * rolling any difficulty "twists" internally from `ctx.rng.twist`/`ctx.tier`.
 *
 * Invariant every pattern MUST uphold: **each consumed onset yields ≥ 1
 * obstacle.** That keeps obstacle count proportional to onset density, which is
 * what makes harder tiers (denser spine) monotonically busier.
 */
export abstract class JumpPattern {
  /** Stable identifier (also used in logs / tests). */
  abstract readonly id: string;
  /** Lowest tier this pattern may appear on. */
  abstract readonly minTier: Variant;
  /** Onsets the pattern needs available to be offered (≥ 1). */
  abstract readonly minAnchors: number;
  /** Base selection weight before contextual scaling. */
  abstract readonly baseWeight: number;

  /** Extra eligibility beyond tier + anchor count (room, onset kind, …). */
  eligible(_ctx: JumpPatternContext): boolean {
    return true;
  }

  /** Selection weight in this context (default: the flat base weight). */
  weight(_ctx: JumpPatternContext): number {
    return this.baseWeight;
  }

  /** Paint the choreography; return how many onsets were claimed. */
  abstract paint(ctx: JumpPatternContext): PatternPlacement;
}
